import React, { useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { visuallyHidden } from "@mui/utils";
import PropTypes from "prop-types";

import {
  Box,
  Chip,
  IconButton,
  TablePagination,
  TableSortLabel,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { UilPlus, UilInfoCircle, UilPen } from "@iconscout/react-unicons";
import Color from "../../enum/Color";
import { formatDate, isNull } from "../../utils/Utils";
import ProjectModal from "../Modal/ProjectModal";
import ProjectTypeEnum from "../../enum/ProjectTypeEnum";
import ProjectManagementStatusEnum from "../../enum/ProjectManagementStatusEnum";
import CreateButton from "../Common/CreateButton";

const statusStyle = (status) => {
  if (status === ProjectManagementStatusEnum.ACTIVE) {
    return {
      backgroundColor: Color.BACKGROUND,
      color: Color.FOREGROUND,
    };
  } else if (status === ProjectManagementStatusEnum.DISABLED) {
    return {
      backgroundColor: Color.DANGER,
      color: Color.FOREGROUND,
    };
  } else {
    return {
      backgroundColor: Color.BACKGROUND,
      color: Color.FOREGROUND,
    };
  }
};

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: "Name",
  },
  {
    id: "clientName",
    numeric: false,
    disablePadding: false,
    label: "Client name",
  },
  {
    id: "startDate",
    numeric: false,
    disablePadding: false,
    label: "Start date",
  },
  {
    id: "endDate",
    numeric: false,
    disablePadding: false,
    label: "End date",
  },
  {
    id: "currency",
    numeric: false,
    disablePadding: false,
    label: "Currency",
  },
  {
    id: "type",
    numeric: false,
    disablePadding: false,
    label: "Type",
  },
  {
    id: "status",
    numeric: false,
    disablePadding: false,
    label: "Status",
  },
  {
    id: "actions",
    numeric: false,
    disablePadding: false,
    label: "",
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, rowCount, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={"left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const EnhancedTableToolbar = () => {
  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
      }}
    >
      <Typography
        sx={{ flex: "1 1 100%" }}
        variant="h6"
        id="tableTitle"
        component="div"
      >
        Projects
      </Typography>
    </Toolbar>
  );
};

const ProjectsTable = (props) => {
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState("");
  const [isReadOnly, setIsReadOnly] = useState(false);

  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("id");
  const [page, setPage] = useState(0);
  const [dense, setDense] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const projects = props.projects;
  const customers = props.customers;

  const closeModalHandler = () => {
    setShowModal((prev) => {
      return !prev;
    });

    props.refreshHandler();
  };

  const showDetails = (rowData, isReadOnly) => {
    setShowModal((prev) => {
      return !prev;
    });
    setModalData(() => {
      return rowData;
    });
    setIsReadOnly(isReadOnly);
  };

  const newModal = () => {
    setModalData(null);
    setShowModal((prev) => {
      return !prev;
    });
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - projects.length) : 0;

  return (
    <div className="Table">
      <ProjectModal
        isVisible={showModal}
        isReadOnly={isReadOnly}
        closeModalHandler={closeModalHandler}
        modalData={modalData}
        customers={customers}
      />

      <CreateButton
        action={newModal}
        icon={<UilPlus />}
        text="New Project"
      />

      <Box sx={{ width: "100%" }}>
        <Paper sx={{ width: "100%", mb: 2 }} elevation={5}>
          <EnhancedTableToolbar />
          <TableContainer>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="tableTitle"
              size={dense ? "small" : "medium"}
            >
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={projects.length}
              />
              <TableBody>
                {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                 rows.slice().sort(getComparator(order, orderBy)) */}
                {stableSort(projects, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow hover tabIndex={-1} key={row.id}>
                        <TableCell align="left">{row.name}</TableCell>
                        <TableCell align="left">
                          {row.customer.companyDisplayName}
                        </TableCell>
                        <TableCell align="left">
                          {formatDate(row.startDate)}
                        </TableCell>
                        <TableCell align="left">
                          {!isNull(row.endDate) && formatDate(row.endDate)}
                        </TableCell>
                        <TableCell align="left">{row.currency}</TableCell>
                        <TableCell align="left">
                          {ProjectTypeEnum[row.projectType]}
                        </TableCell>
                        <TableCell align="left">
                          <Chip
                            sx={{ width: "100px" }}
                            label={row.status}
                            style={statusStyle(row.status)}
                          />
                        </TableCell>
                        <TableCell align={"left"} className="Details">
                          <Tooltip title="Customer info">
                            <IconButton onClick={() => showDetails(row, true)}>
                              <UilInfoCircle
                                style={{ color: Color.BACKGROUND }}
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit customer">
                            <IconButton onClick={() => showDetails(row, false)}>
                              <UilPen style={{ color: Color.BACKGROUND }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: (dense ? 33 : 53) * emptyRows,
                    }}
                  >
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={projects.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>
    </div>
  );
};

export default ProjectsTable;
